import React from "react";
import { TopNav } from "../../../components/Navbar/Webpages/TopNav";
import Footer from "../../../components/Layout/Footer";

// Icons
import {
  UserCircleIcon,
  MailIcon,
  ChatAlt2Icon,
} from "@heroicons/react/outline";
import {
  BsWhatsapp,
  BsGeoAltFill,
  BsEnvelopeFill,
  BsArrowRight,
} from "react-icons/bs";

const ContactInfoItem = ({ icon: Icon, title, content, subContent }) => (
  <div className="flex items-start gap-4 mb-8 group">
    <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary text-xl shrink-0 group-hover:scale-110 transition-transform duration-300">
      <Icon />
    </div>
    <div>
      <h4 className="text-lg font-heading font-bold text-gray-900 mb-1">
        {title}
      </h4>
      <p className="text-gray-500 font-body text-sm leading-relaxed">
        {content}
      </p>
      {subContent && (
        <p className="text-gray-500 font-body text-sm leading-relaxed">
          {subContent}
        </p>
      )}
    </div>
  </div>
);

const InputField = ({ icon: Icon, type, placeholder, name, isTextArea }) => (
  <div className="relative">
    <div className="absolute top-4 left-4 text-gray-400 pointer-events-none">
      <Icon className="h-5 w-5" />
    </div>
    {isTextArea ? (
      <textarea
        name={name}
        rows="4"
        placeholder={placeholder}
        className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all outline-none font-body text-gray-700 placeholder-gray-400 resize-none"
      />
    ) : (
      <input
        type={type}
        name={name}
        placeholder={placeholder}
        className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all outline-none font-body text-gray-700 placeholder-gray-400"
      />
    )}
  </div>
);

const Support = () => {
  return (
    <div className="bg-white overflow-x-hidden selection:bg-primary/20 selection:text-primary">
      <TopNav />

      {/* --- HERO SECTION --- */}
      <div className="relative h-[45vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        {/* 1. Background Image (Support/Headset Context) */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1534536281715-e28d76689b4d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80"
            alt="Customer Support"
            className="w-full h-full object-cover"
          />
        </div>

        {/* 2. FULL PRIMARY OVERLAY */}
        <div className="absolute inset-0 bg-primary/90 mix-blend-multiply z-10"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent z-10"></div>

        {/* 3. Content */}
        <div className="relative z-20 container max-w-screen-xl mx-auto px-4 text-center mt-8">
          <span className="inline-block py-1 px-4 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white text-xs font-heading font-bold tracking-widest uppercase mb-6">
            Contact Us
          </span>

          <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6 tracking-tight drop-shadow-sm">
            We're Here <br />
            <span className="text-blue-200">To Help You</span>
          </h1>

          <p className="font-body text-lg md:text-xl text-white/90 max-w-2xl mx-auto font-light leading-relaxed">
            Our team is available 24/7 to answer your questions and help you
            with your investment journey.
          </p>
        </div>
      </div>

      {/* --- MAIN CONTENT SECTION --- */}
      <section className="py-20 md:py-28 relative">
        <div className="container max-w-screen-xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-24">
            {/* LEFT COLUMN: Contact Form */}
            <div data-aos="fade-right">
              <div className="bg-white rounded-3xl p-8 shadow-2xl shadow-primary/5 border border-gray-100 relative overflow-hidden">
                {/* Decorative blob in corner */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-bl-full -z-0"></div>

                <div className="relative z-10">
                  <h3 className="text-2xl font-heading font-bold text-gray-900 mb-2">
                    Send a Message
                  </h3>
                  <p className="text-gray-500 font-body text-sm mb-8">
                    Fill out the form below and we will get back to you shortly.
                  </p>

                  <form className="space-y-5">
                    <InputField
                      icon={UserCircleIcon}
                      type="text"
                      name="name"
                      placeholder="Your Full Name"
                    />

                    <InputField
                      icon={MailIcon}
                      type="email"
                      name="email"
                      placeholder="Email Address"
                    />

                    <InputField
                      icon={ChatAlt2Icon}
                      isTextArea
                      name="message"
                      placeholder="How can we help you?"
                    />

                    <button
                      type="submit"
                      className="w-full bg-primary text-white py-4 rounded-xl font-heading font-bold text-lg shadow-lg shadow-primary/30 hover:bg-primary/90 hover:shadow-primary/50 hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2"
                    >
                      Send Message <BsArrowRight />
                    </button>
                  </form>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN: Info & Context */}
            <div className="self-center" data-aos="fade-left">
              <span className="text-primary font-heading font-bold tracking-wider uppercase text-sm">
                Support
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mt-2 mb-6">
                Get in{" "}
                <span className="text-primary relative inline-block">
                  Touch
                  <svg
                    className="absolute w-full h-2 -bottom-1 left-0 text-primary/20 -z-10"
                    viewBox="0 0 100 10"
                    preserveAspectRatio="none"
                  >
                    <path
                      d="M0 5 Q 50 10 100 5"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                    />
                  </svg>
                </span>{" "}
                with us
              </h2>

              <p className="font-body text-gray-600 text-lg leading-relaxed mb-10">
                Our Customer Support service is available 24/7. Whether you have
                questions about our plans, payments, or just want to say hello,
                we are here for you.
              </p>

              <div className="space-y-2">
                <ContactInfoItem
                  icon={BsGeoAltFill}
                  title="Headquarters"
                  content="191 Coldharbour Ln, London,"
                  subContent="United Kingdom, SE5 9PA"
                />

                <ContactInfoItem
                  icon={BsWhatsapp}
                  title="WhatsApp Support"
                  content="+1 (804) 453-8796"
                />

                <ContactInfoItem
                  icon={BsEnvelopeFill}
                  title="Email Us"
                  content="zamchainadvanceinvestment@support.com"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Support;
